import { useEffect, MouseEvent } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import KmxLocationStroked from '@kmx/mui-icons/KmxLocationStroked';

import { CarTilesCarousel } from '../../../components/carousels/car-tiles/CarTilesCarousel';
import { ANALYTICS_CONSTANTS, buildModuleExperience, trackAnchorClick, trackEDDL } from '../../../utilities/analytics';
import { CarouselButtons } from '../../../components/CarouselButtons';
import { KmxContainer } from '../../../components/KmxContainer';
import useAppState from '../../../common/state/appState';
import LocalCarsPlaceholderCarousel from './LocalCarsPlaceholderCarousel';
import './LocalCars.scss';

const carouselId = 'local-cars-carousel';

export const LocalCars = () => {
    const recommendedVehicles = useAppState((state) => state.recommendedVehicles);
    const setRecommendedVehicles = useAppState((state) => state.setRecommendedVehicles);
    const isRecommendedVehiclesLoading = useAppState((state) => state.isRecommendedVehiclesLoading);
    const setFavoriteVehicles = useAppState((state) => state.setFavoriteVehicles);
    const setCiam = useAppState((state) => state.setCiam);
    const zipCode = useAppState((state) => state.zipCode);
    const cohortId = useAppState((state) => state.recommendationsCohortId);
    const testId = useAppState((state) => state.recommendationsTestId);

    useEffect(() => {
        setCiam();
        setRecommendedVehicles();
        setFavoriteVehicles();
    }, [setCiam, setRecommendedVehicles, setFavoriteVehicles]);

    useEffect(() => {
        if (recommendedVehicles?.length && !isRecommendedVehiclesLoading) {
            trackEDDL({
                event: ANALYTICS_CONSTANTS.EVENTS.IMPRESSION.RECOMMENDED_CAR,
                recommendations: {
                    stockNumber: '',
                    carouselLength: String(recommendedVehicles.length),
                    browse: recommendedVehicles.map((vehicle) => vehicle.stockNumber).join('|'),
                },
                module: {
                    moduleExperience: buildModuleExperience('VR|Homepage|Shop cars near me', cohortId, testId),
                },
            });
        }
    }, [recommendedVehicles, isRecommendedVehiclesLoading, cohortId, testId]);

    const openStoreSelectionModal = () => {
        const detail = { stateOrZip: zipCode };

        // Analytics
        trackEDDL({
            event: 'storeChange',
            linkDetails: {
                name: zipCode || '',
                position: 'Shop cars near you',
            },
        });

        // Open modal
        const event = new CustomEvent('kmxOpenStoreSelectionModal', { detail });
        document.documentElement.dispatchEvent(event);
    };

    const handleLocalCarsClick = (e: MouseEvent) => {
        trackAnchorClick({
            clickEvent: e,
            taggingEvent: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
            title: 'shop all local cars',
            position: 'shop cars near me',
            href: '/cars?distance=50',
            page: 'Homepage',
            search: {
                initiationMethod: `Homepage|shop all local cars|shop cars near me`,
            },
        });
    };

    if (!isRecommendedVehiclesLoading && (!recommendedVehicles || recommendedVehicles.length === 0)) {
        return <></>;
    }

    return (
        <>
            <KmxContainer maxWidth="xl" sx={{ pt: { xs: 5, md: 7 } }}>
                <Stack spacing={2}>
                    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Typography component="h2" sx={{ typography: { xs: 'h5', md: 'h4' } }}>
                                Cars near me
                            </Typography>
                            <Chip
                                label={zipCode}
                                variant="outlined"
                                icon={<KmxLocationStroked color="secondary" />}
                                onClick={openStoreSelectionModal}
                                sx={{ borderColor: '#CCD0D6' }}
                            />
                        </Stack>
                        <Stack direction="row" spacing={2} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleLocalCarsClick}
                                sx={{ borderColor: '#CCD0D6' }}
                            >
                                Shop all local cars
                            </Button>
                            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                <CarouselButtons sectionIdPrefix={carouselId} ariaText="local cars" />
                            </Box>
                        </Stack>
                    </Stack>

                    {isRecommendedVehiclesLoading ? (
                        <LocalCarsPlaceholderCarousel />
                    ) : (
                        <CarTilesCarousel
                            sectionName="Shop cars near me"
                            carouselId={carouselId}
                            vehiclesData={recommendedVehicles.slice(0, 6)}
                            nextFocus="online-offer"
                            isRemoveEnabled={false}
                            showRelatedSearchesTile
                            recommenderCohortId={cohortId}
                            recommenderTestId={testId}
                            disableNavButtons
                        />
                    )}

                    <Button
                        size="large"
                        variant="outlined"
                        fullWidth
                        onClick={handleLocalCarsClick}
                        sx={{ display: { sm: 'none' } }}
                    >
                        Shop all local cars
                    </Button>
                </Stack>
            </KmxContainer>
        </>
    );
};
